// chatService.js
// Purpose: Enhanced CRUD operations and cache logic for chat sessions and messages in Supabase.
// Main functions: getChats, createChat, deleteChat, getMessages, createMessage, updateSessionTimestamp
// Features: PERMANENT CACHE + INCREMENTAL SYNC for optimal performance and cost efficiency
//
// CACHING STRATEGY OVERVIEW:
// ========================
// Traditional Approach (OLD): Cache expires every 5 minutes → Full refetch of ALL data
// New Approach (IMPLEMENTED): Permanent cache + incremental sync for new data only
//
// BENEFITS:
// - Instant loading: Data always available from cache
// - Cost efficient: Only fetch new/changed records since last sync
// - Bandwidth friendly: Minimal data transfer
// - Offline resilient: Works with cached data when offline
// - Real-time feel: Background sync keeps data fresh
//
// HOW IT WORKS:
// 1. First load: Full fetch → Store in permanent cache with sync timestamp
// 2. Subsequent loads: Return cached data instantly → Background incremental sync
// 3. Incremental sync: Only fetch records newer than lastSync timestamp
// 4. New data creation: Add directly to cache (no clearing)
// 5. Fallback: Full refresh if incremental sync fails repeatedly
//
// Inputs: userId, sessionId, message data, cacheManager, supabase

import { supabase } from '../../config/supabase';
// Use centralized cacheManager to avoid duplicate instances. Prefer this path across services.
import logger from '../../utils/logger';
import { cacheManager } from '../cacheManager';
import { ChatMessageInput } from '../../lib/schemas/chat';
// Import activity tracking helpers for chat_message events
import { getUuidSessionId, getAppSessionId } from '../../utils/activityEvents';

/**
 * Get all chat sessions for a user with permanent cache and incremental sync
 *
 * PERMANENT CACHE STRATEGY FOR CHAT SESSIONS:
 * ==========================================
 * This function implements the same sophisticated caching strategy used for messages,
 * goals, tasks, and plans - providing instant loading and cost-efficient data fetching.
 *
 * FLOW:
 * 1. Session validation with timeout (prevents hanging)
 * 2. Check permanent cache first - if data exists, return immediately (instant loading)
 * 3. Trigger background incremental sync to fetch only new/updated sessions since last sync
 * 4. If no cache exists, perform full fetch and establish cache baseline
 * 5. Robust fallback: return cached data even if fresh fetch fails
 *
 * INCREMENTAL SYNC LOGIC:
 * - Uses lastSync timestamp to query only sessions newer/updated than cache
 * - Merges new/updated sessions with existing cache (no duplicates)
 * - Updates sync timestamp for next incremental fetch
 * - Falls back to full refresh if incremental sync fails repeatedly
 *
 * CHAT SESSIONS SPECIAL CONSIDERATIONS:
 * - Sessions are updated frequently (last_message_at, updated_at)
 * - Incremental sync uses OR condition: created_at > lastSync OR updated_at > lastSync
 * - This captures both new chat sessions and existing sessions with new activity
 * - Essential for showing recent activity and proper session ordering
 *
 * @param {string} userId - User ID
 * @param {{ forceRefresh?: boolean, signal?: AbortSignal, timeoutMs?: number }} [options] - Options including forceRefresh
 * @returns {Promise<any[]>} Array of chat sessions
 */
export async function getChats(userId, { forceRefresh = false, signal } = {}) {
  if (!userId) {
    logger.warn('⚠️ chatService.getChats: No userId provided');
    return [];
  }

  // STEP 0: SESSION VALIDATION
  // =========================
  // Check if we have a valid session before making the query (with timeout)
  // This prevents the app from hanging on session checks
  try {
    // Prefer AuthContext-provided session via window.currentUser to avoid hangs
    const session = await checkSessionWithTimeout();
    if (!session?.user) {
      logger.warn(
        '⚠️ chatService.getChats: No valid session from checkSessionWithTimeout, skipping query',
      );
      return [];
    }
  } catch (error) {
    const e = /** @type {any} */ (error);
    logger.warn('⚠️ chatService.getChats: Session validation failed:', e?.message || e);
    return [];
  }

  const cacheKey = 'chats';
  logger.info('💬 chatService.getChats: Fetching chats for user:', userId, { forceRefresh });

  // STEP 1: PERMANENT CACHE CHECK
  // =============================
  // Check if we have cached data (permanent cache - always return if available)
  // This provides instant loading for users - no waiting for API calls
  if (!forceRefresh && cacheManager.hasData(cacheKey, userId)) {
    const cached = /** @type {any[]} */ (cacheManager.get(cacheKey, userId) || []);
    if (cached && Array.isArray(cached)) {
      logger.info('✅ chatService.getChats: Returning cached chats:', cached.length);

      return cached;
    }
  }

  // STEP 2: FULL FETCH (NO CACHE OR FORCE REFRESH)
  // ==============================================
  // No cache exists or user explicitly requested fresh data
  // Perform full fetch to establish cache baseline
  try {
    logger.info('🔄 chatService.getChats: Performing full fetch (no cache or force refresh)');
    return await performChatsFullFetch(userId, cacheKey, signal);
  } catch (error) {
    logger.error('❌ chatService.getChats: Full fetch failed:', error, {
      hasBaseline: Boolean(cacheManager.getLastSyncTimestamp(cacheKey, userId)),
    });

    // FALLBACK STRATEGY
    // ================
    // If fresh fetch fails, return cached data if available (even if potentially stale)
    // This ensures app remains functional even with network issues
    const fallbackCache = /** @type {any[]} */ (cacheManager.get(cacheKey, userId) || []);
    if (fallbackCache && Array.isArray(fallbackCache)) {
      logger.info('🔄 chatService.getChats: Returning fallback cached data:', fallbackCache.length);
      return fallbackCache;
    }

    // No cache available, return empty array
    logger.warn('⚠️ chatService.getChats: No cache available, returning empty array');
    return [];
  }
}

// Incremental sync removed: realtime + full fetch establishes and updates cache baseline

/**
 * Perform full fetch of all chat sessions for a user
 *
 * FULL FETCH STRATEGY FOR CHAT SESSIONS:
 * ======================================
 * This function is used when:
 * 1. No cache exists (first time loading chat sessions)
 * 2. User explicitly requests fresh data (forceRefresh=true)
 * 3. Incremental sync has failed multiple times (fallback)
 *
 * ESTABLISHES CACHE BASELINE:
 * - Fetches ALL chat sessions for the user
 * - Stores them in permanent cache
 * - Sets sync timestamp for future incremental syncs
 * - This becomes the foundation for all future incremental updates
 *
 * @param {string} userId - User ID
 * @param {string} cacheKey - Cache key for chat sessions
 * @param {AbortSignal} [signal] - Abort signal
 * @returns {Promise<any[]>} Array of chat sessions
 */
async function performChatsFullFetch(userId, cacheKey, signal) {
  logger.info('🔄 chatService.performChatsFullFetch: Starting full fetch from Supabase');

  // FULL FETCH QUERY WITH MESSAGE COUNT
  // ===================================
  // Fetch chat sessions with message count to filter out empty sessions.
  // This prevents empty chats from appearing in the UI.
  let query = supabase
    .from('chat_sessions')
    .select('*, chat_messages(count)')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(100); // Fetch more to account for filtering
  if (signal) query = query.abortSignal(signal);
  const { data, error } = await query;

  if (error) {
    logger.error('❌ chatService.performChatsFullFetch: Supabase error:', error);
    throw error;
  }

  // FILTER EMPTY SESSIONS
  // =====================
  // Remove sessions with zero messages (defensive measure)
  const sessionsWithMessages = (Array.isArray(data) ? data : []).filter((session) => {
    const msgData = session?.chat_messages;
    let messageCount = 0;
    if (Array.isArray(msgData) && msgData.length > 0) {
      messageCount = msgData[0]?.count ?? 0;
    } else if (typeof msgData?.count === 'number') {
      messageCount = msgData.count;
    }
    return messageCount > 0;
  });

  // Clean up by removing the chat_messages join field
  const cleanedSessions = sessionsWithMessages.slice(0, 50).map((s) => {
    const { chat_messages, ...rest } = s;
    return rest;
  });

  logger.info(
    '✅ chatService.performChatsFullFetch: Fetched from Supabase:',
    data?.length || 0,
    'total,',
    cleanedSessions.length,
    'with messages',
  );

  // ESTABLISH CACHE BASELINE
  // =======================
  // Store sessions with messages in permanent cache with current timestamp
  // This timestamp becomes the reference point for future incremental syncs
  const syncTimestamp = new Date().toISOString();
  cacheManager.mergeIncremental(cacheKey, cleanedSessions, userId, syncTimestamp);

  logger.info(
    '✅ chatService.performChatsFullFetch: Cached',
    cleanedSessions.length,
    'chat sessions with sync timestamp:',
    syncTimestamp,
  );
  return cleanedSessions;
}

/**
 * Create a new chat session and add it to permanent cache
 * @param {{ user_id: string, title?: string } & Record<string, any>} chatData - Chat session data (must include user_id)
 * @returns {Promise<object>} Created chat session
 */
export async function createChat(chatData) {
  const { user_id, title, ...otherData } = chatData;

  if (!user_id) {
    throw new Error('user_id is required to create a chat session');
  }

  logger.info('💬 chatService.createChat: Creating new chat session:', { user_id, title });

  const { data, error } = await supabase
    .from('chat_sessions')
    .insert([
      {
        user_id,
        title:
          title ||
          new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
        ...otherData,
      },
    ])
    .select()
    .single();

  if (error) {
    logger.error('❌ chatService.createChat: Supabase error:', error);
    throw error;
  }

  logger.info('✅ chatService.createChat: Created chat session:', data.id);

  // ADD TO PERMANENT CACHE
  // =====================
  // Add new chat session directly to cache instead of clearing it
  // This maintains instant loading while keeping data fresh
  const cacheKey = 'chats';
  const syncTimestamp = new Date().toISOString();
  cacheManager.mergeIncremental(cacheKey, [data], user_id, syncTimestamp);

  logger.info('✅ chatService.createChat: Added new chat session to permanent cache');
  return data;
}

/**
 * Update a chat session (e.g., rename title)
 * @param {string} chatId - Chat session ID
 * @param {string} userId - User ID for security
 * @param {{ title?: string } & Record<string, any>} updateData - Data to update
 * @returns {Promise<object>} Updated chat session
 */
export async function updateChat(chatId, userId, updateData) {
  if (!chatId || !userId) {
    throw new Error('chatId and userId are required to update a chat session');
  }

  if (!updateData || Object.keys(updateData).length === 0) {
    throw new Error('updateData is required and must contain at least one field to update');
  }

  logger.info('💬 chatService.updateChat: Updating chat session:', chatId, updateData);

  // First check if the session exists and belongs to the user
  const { data: existingSession, error: selectError } = await supabase
    .from('chat_sessions')
    .select('*')
    .eq('id', chatId)
    .eq('user_id', userId)
    .single();

  if (selectError) {
    logger.error('❌ chatService.updateChat: Session not found or access denied:', selectError);
    throw selectError;
  }

  if (!existingSession) {
    throw new Error('Chat session not found or access denied');
  }

  // Update the session
  const { data, error } = await supabase
    .from('chat_sessions')
    .update({
      ...updateData,
      updated_at: new Date().toISOString(),
    })
    .eq('id', chatId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    logger.error('❌ chatService.updateChat: Supabase error:', error);
    throw error;
  }

  logger.info('✅ chatService.updateChat: Updated chat session:', data.id);

  // UPDATE PERMANENT CACHE
  // =====================
  // Update the chat session in cache instead of clearing it
  // This maintains instant loading while keeping data fresh
  const cacheKey = 'chats';
  try {
    const chats = /** @type {any[]|null} */ (cacheManager.get(cacheKey, userId));
    if (Array.isArray(chats)) {
      const updatedChats = chats.map((chat) =>
        String(chat?.id) === String(chatId) ? { ...chat, ...data } : chat,
      );
      cacheManager.set(cacheKey, updatedChats, userId);
      logger.info('✅ chatService.updateChat: Updated chat session in permanent cache');
    }
  } catch (e) {
    logger.warn('chatService.updateChat: Failed to update chats cache (non-blocking):', e);
  }

  return data;
}

/**
 * Delete a chat session and all its messages
 * @param {string} chatId - Chat session ID
 * @param {string} userId - User ID for security
 * @returns {Promise<boolean>} Success status
 */
export async function deleteChat(chatId, userId) {
  if (!chatId || !userId) {
    throw new Error('chatId and userId are required to delete a chat session');
  }

  logger.info('💬 chatService.deleteChat: Deleting chat session:', chatId);

  // First delete all messages in the session
  const { error: messagesError } = await supabase
    .from('chat_messages')
    .delete()
    .eq('session_id', chatId); // chat_messages has no user_id column; RLS should enforce ownership

  if (messagesError) {
    logger.error('❌ chatService.deleteChat: Error deleting messages:', messagesError);
    throw messagesError;
  }

  // Then delete the session
  const { error } = await supabase
    .from('chat_sessions')
    .delete()
    .eq('id', chatId)
    .eq('user_id', userId);

  if (error) {
    logger.error('❌ chatService.deleteChat: Supabase error:', error);
    throw error;
  }

  logger.info('✅ chatService.deleteChat: Deleted chat session and messages');

  // Item-level cache removal to avoid clearing entire lists
  try {
    const chats = /** @type {any[]|null} */ (cacheManager.get('chats', userId));
    const nextChats = Array.isArray(chats)
      ? chats.filter((s) => String(s?.id) !== String(chatId))
      : [];
    cacheManager.set('chats', nextChats, userId);
  } catch (e) {
    logger.warn('chatService.deleteChat: Failed to prune chats cache (non-blocking):', e);
  }
  try {
    cacheManager.clear(userId, `messages_${chatId}`);
  } catch (e) {
    logger.warn('chatService.deleteChat: Failed to clear messages cache (non-blocking):', e);
  }
  return true;
}

/**
 * Get all messages for a chat session with permanent cache and incremental sync
 *
 * PERMANENT CACHE STRATEGY:
 * ========================
 * This function implements a sophisticated caching strategy that eliminates the need for
 * repeated full data fetches, dramatically improving performance and reducing API costs.
 *
 * FLOW:
 * 1. Check permanent cache first - if data exists, return immediately (instant loading)
 * 2. Trigger background incremental sync to fetch only new messages since last sync
 * 3. If no cache exists, perform full fetch and establish cache baseline
 * 4. Robust fallback: return cached data even if fresh fetch fails
 *
 * INCREMENTAL SYNC LOGIC:
 * - Uses lastSync timestamp to query only messages newer than cache
 * - Merges new messages with existing cache (no duplicates)
 * - Updates sync timestamp for next incremental fetch
 * - Falls back to full refresh if incremental sync fails repeatedly
 *
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID for security
 * @param {{ forceRefresh?: boolean, signal?: AbortSignal, timeoutMs?: number }} [options] - Options including forceRefresh
 * @returns {Promise<any[]>} Array of chat messages
 */
export async function getMessages(
  sessionId,
  userId,
  { forceRefresh = false, signal, timeoutMs } = {},
) {
  if (!userId || !sessionId) {
    logger.warn('⚠️ chatService.getMessages: Missing userId or sessionId');
    return [];
  }

  const cacheKey = `messages_${sessionId}`;
  logger.info('💬 chatService.getMessages: Fetching messages for session:', sessionId, {
    forceRefresh,
  });

  // STEP 1: PERMANENT CACHE CHECK
  // =============================
  // Check if we have cached data (permanent cache - always return if available)
  // This provides instant loading for users - no waiting for API calls
  if (!forceRefresh && cacheManager.hasData(cacheKey, userId)) {
    const cached = /** @type {any[]} */ (cacheManager.get(cacheKey, userId) || []);
    if (cached && Array.isArray(cached)) {
      logger.info('✅ chatService.getMessages: Returning cached messages:', cached.length);

      return cached;
    }
  }

  // STEP 2: FULL FETCH (NO CACHE OR FORCE REFRESH)
  // ==============================================
  // No cache exists or user explicitly requested fresh data
  // Perform full fetch to establish cache baseline
  try {
    logger.info('🔄 chatService.getMessages: Performing full fetch (no cache or force refresh)');
    return await performFullFetch(sessionId, userId, cacheKey, signal, timeoutMs);
  } catch (error) {
    const e = /** @type {any} */ (error);
    logger.error('❌ chatService.getMessages: Full fetch failed:', e, {
      hasBaseline: Boolean(cacheManager.getLastSyncTimestamp(cacheKey, userId)),
    });

    // FALLBACK STRATEGY
    // ================
    // If fresh fetch fails, return cached data if available (even if potentially stale)
    // This ensures app remains functional even with network issues
    const fallbackCache = /** @type {any[]} */ (cacheManager.get(cacheKey, userId) || []);
    if (fallbackCache && Array.isArray(fallbackCache)) {
      logger.info(
        '🔄 chatService.getMessages: Returning fallback cached data:',
        fallbackCache.length,
      );
      return fallbackCache;
    }

    // No cache available, return empty array
    logger.warn('⚠️ chatService.getMessages: No cache available, returning empty array');
    return [];
  }
}

/**

    // INCREMENTAL QUERY
    // ================
    // This is the magic: only fetch messages created AFTER our last sync
    // Uses `gt('created_at', lastSync)` to filter for new messages only
    let query = supabase
      .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .gt('created_at', lastSync) // 🎯 KEY OPTIMIZATION: Only new messages!
      .order('created_at', { ascending: true });
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;

    // ERROR HANDLING
    // =============
    // If incremental sync fails, record the failure
    // After multiple failures, system will fall back to full refresh
    if (error) {
      logger.error('❌ chatService.performIncrementalSync: Query error:', error);
      cacheManager.recordIncrementalFailure(cacheKey, userId);
      return;
    }

    // NO NEW DATA
    // ===========
    // If no new messages since last sync, we're done (cache is up to date)
    if (!data || data.length === 0) {
      logger.debug('✅ chatService.performIncrementalSync: No new messages since last sync');
      return;
    }

    // DATA TRANSFORMATION
    // ==================
    // Map database fields to expected format (same as full fetch)
    const mappedMessages = data.map((msg) => ({
      id: msg.id,
      session_id: msg.session_id,
      role: msg.sender, // Map 'sender' to 'role'
      content: msg.message?.content || msg.message, // Extract content from message object
      created_at: msg.created_at,
      correlation_id: msg.correlation_id, // Include correlation ID for deduplication
    }));

    // CACHE MERGE
    // ===========
    // Merge new messages with existing cache (no duplicates)
    // Update sync timestamp for next incremental fetch
    const syncTimestamp = new Date().toISOString();
    cacheManager.mergeIncremental(cacheKey, mappedMessages, userId, syncTimestamp);

    logger.info(
      '✅ chatService.performIncrementalSync: Successfully synced',
      mappedMessages.length,
      'new messages',
    );
  } catch (error) {
    logger.error('❌ chatService.performIncrementalSync: Unexpected error:', error);
    cacheManager.recordIncrementalFailure(cacheKey, userId);
  }
}

/**
 * Perform full fetch of all messages for a session
 *
 * FULL FETCH STRATEGY:
 * ===================
 * This function is used when:
 * 1. No cache exists (first time loading a session)
 * 2. User explicitly requests fresh data (forceRefresh=true)
 * 3. Incremental sync has failed multiple times (fallback)
 *
 * ESTABLISHES CACHE BASELINE:
 * - Fetches ALL messages for the session
 * - Stores them in permanent cache
 * - Sets sync timestamp for future incremental syncs
 * - This becomes the foundation for all future incremental updates
 *
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @param {string} cacheKey - Cache key for messages
 * @param {AbortSignal} [signal] - Abort signal
 * @param {number} [timeoutMs] - Optional timeout override in ms (defaults to 8000)
 * @returns {Promise<any[]>} Array of chat messages
 */
async function performFullFetch(sessionId, userId, cacheKey, signal, timeoutMs) {
  // SESSION VALIDATION
  // =================
  // Ensure we have a valid Supabase session before making queries
  const session = await checkSessionWithTimeout();
  if (!session) {
    throw new Error('No valid session available');
  }

  logger.info('🔄 chatService.performFullFetch: Starting full fetch from Supabase');
  cacheManager.markFullFetchAttempt(cacheKey, userId);

  // TIMEOUT PROTECTION
  // =================
  // Prevent hanging queries with 8-second timeout
  // This ensures app remains responsive even if Supabase is slow
  const effectiveTimeout = typeof timeoutMs === 'number' && timeoutMs > 0 ? timeoutMs : 8000;
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(
        new Error(
          `SUPABASE_TIMEOUT: Query timed out after ${Math.round(effectiveTimeout / 1000)} seconds`,
        ),
      );
    }, effectiveTimeout);
  });

  // FULL FETCH QUERY
  // ===============
  // Fetch ALL messages for this session (no filtering)
  // This establishes the complete baseline for future incremental syncs
  let q = supabase
    .from('chat_messages')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });
  if (signal) q = q.abortSignal(signal);
  const queryPromise = q;

  // Race between the query and timeout
  const { data, error } = /** @type {{ data: any[]; error: any }} */ (
    await Promise.race([queryPromise, timeoutPromise])
  );

  if (error) {
    logger.error('❌ chatService.performFullFetch: Supabase error:', error);
    throw error;
  }

  logger.info(
    '✅ chatService.performFullFetch: Fetched from Supabase:',
    data?.length || 0,
    'messages',
  );

  // DATA TRANSFORMATION
  // ==================
  // Map database fields to expected format
  const mappedMessages = (data || []).map((msg /** @type {any} */) => ({
    id: msg.id,
    session_id: msg.session_id,
    role: msg.sender, // Map 'sender' to 'role'
    content: msg.message?.content || msg.message, // Extract content from message object
    created_at: msg.created_at,
    correlation_id: msg.correlation_id, // Include correlation ID for deduplication
  }));

  // ESTABLISH CACHE BASELINE
  // =======================
  // Store ALL messages in permanent cache with current timestamp
  // This timestamp becomes the reference point for future incremental syncs
  // Future syncs will only fetch messages newer than this timestamp
  const syncTimestamp = new Date().toISOString();
  cacheManager.mergeIncremental(cacheKey, mappedMessages, userId, syncTimestamp);

  logger.info(
    '✅ chatService.performFullFetch: Cached',
    mappedMessages.length,
    'messages with sync timestamp:',
    syncTimestamp,
  );
  return mappedMessages;
}

/**
 * Check session validity fast with fallback timeout
 * Priority:
 * 1) Use window.currentUser (set by AuthContext on SIGNED_IN) to avoid getSession hang
 * 2) Fallback to supabase.auth.getSession() with 3s timeout
 * @returns {Promise<{ user?: any } | null>} Session-like object or null if invalid/timeout
 */
async function checkSessionWithTimeout() {
  try {
    // Fast path: use current authenticated user provided by AuthContext
    if (typeof window !== 'undefined' && window.currentUser?.id) {
      const user = { id: window.currentUser.id };
      // Return a minimal session-like object expected by callers
      logger.debug('✅ chatService.checkSessionWithTimeout: Using window.currentUser for session');
      return { user };
    }

    logger.debug(
      '🔍 chatService.checkSessionWithTimeout: Falling back to getSession() with 3s timeout...',
    );
    const getSessionPromise = supabase.auth
      .getSession()
      .then((res) => res)
      .catch((e) => ({ data: { session: null }, error: e }));
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Session check timeout')), 3000),
    );

    const result = /** @type {{ data?: { session?: any }; error?: any }} */ (
      await Promise.race([getSessionPromise, timeoutPromise])
    );
    const session = result?.data?.session;
    const sessionError = result?.error;

    if (sessionError) {
      logger.error('❌ chatService.checkSessionWithTimeout: Session error:', sessionError);
      return null;
    }
    if (!session?.user) {
      logger.warn('⚠️ chatService.checkSessionWithTimeout: No valid session found');
      return null;
    }
    return session;
  } catch (error) {
    const e = /** @type {any} */ (error);
    if (e?.message === 'Session check timeout') {
      logger.warn('⏱️ chatService.checkSessionWithTimeout: Session check timed out');
    } else {
      logger.error('❌ chatService.checkSessionWithTimeout: Unexpected error:', e);
    }
    return null;
  }
}

/**
 * Create a new message in a chat session
 * @param {{ content: string, role: string, session_id: string, user_id: string } & Record<string, any>} messageData - Message data (must include content, role, session_id, user_id)
 * @returns {Promise<object>} Created message
 */
export async function createMessage(messageData) {
  // Validate input with Zod for safety
  const parsed = ChatMessageInput.safeParse(messageData);
  if (!parsed.success) {
    logger.warn('chatService.createMessage: Invalid input', {
      issues: parsed.error.issues?.slice(0, 3),
    });
    throw new Error('Invalid message payload');
  }
  const { content, role, session_id, user_id, ...otherData } = parsed.data;

  if (!content || !role || !session_id || !user_id) {
    throw new Error('content, role, session_id, and user_id are required to create a message');
  }

  logger.info(' chatService.createMessage: Creating message:', {
    role,
    session_id,
    contentLength: content.length,
  });

  // Helper to attempt an insert with a timeout and normalize results
  /** @param {number} attempt */
  const attemptInsert = async (attempt) => {
    const TIMEOUT_MS = 12000; // 12s per attempt to reduce perceived timeouts
    const startedAt = Date.now();
    logger.info(
      ` chatService.createMessage: Supabase insert attempt ${attempt} with ${TIMEOUT_MS / 1000}s timeout`,
    );
    // Timeout promise (12s)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`SUPABASE_TIMEOUT: Insert timed out after ${TIMEOUT_MS / 1000} seconds`));
      }, TIMEOUT_MS);
    });

    // Supabase insert promise with correct schema
    const insertPromise = supabase
      .from('chat_messages')
      .insert([
        {
          message: { type: role, content },
          sender: role,
          session_id,
          // Important: chat_messages table DOES have user_id (FK to auth.users)
          // We include it to satisfy RLS policies that may check chat_messages.user_id
          user_id,
          ...otherData,
        },
      ])
      .select()
      .single();

    try {
      const { data, error } = await Promise.race([insertPromise, timeoutPromise]);
      const elapsed = Date.now() - startedAt;
      logger.debug(` chatService.createMessage: Attempt ${attempt} completed in ${elapsed}ms`);
      return { data, error };
    } catch (e) {
      // Normalize thrown errors (e.g., from timeout) into { data:null, error }
      return { data: null, error: e };
    }
  };

  try {
    let { data, error } = await attemptInsert(1);

    // Retry once if timeout occurred
    if (
      !data &&
      error?.message &&
      (error.message.includes('timeout') || error.message.includes('SUPABASE_TIMEOUT'))
    ) {
      logger.warn('⏱️ chatService.createMessage: Timeout on attempt 1, retrying once...');
      ({ data, error } = await attemptInsert(2));
    }

    if (error) {
      logger.error('❌ chatService.createMessage: Supabase error:', error);
      throw error;
    }

    logger.info('✅ chatService.createMessage: Created message:', data.id);

    // Map database response to expected format
    const mappedMessage = {
      id: data.id,
      session_id: data.session_id,
      role: data.sender, // Map 'sender' to 'role'
      content: data.message?.content || data.message, // Extract content from message object
      created_at: data.created_at,
    };

    // Update session timestamp to reflect recent activity
    await updateSessionTimestamp(session_id, user_id);

    // Add new message to permanent cache instead of clearing it
    const cacheKey = `messages_${session_id}`;
    const syncTimestamp = new Date().toISOString();
    cacheManager.mergeIncremental(cacheKey, [mappedMessage], user_id, syncTimestamp);

    // Also clear chats cache since session updated_at changed (chats still use old system)
    cacheManager.clear(user_id, 'chats');

    logger.info('✅ chatService.createMessage: Added new message to permanent cache');

    // Record activity event for user messages only (not assistant replies)
    // This populates user_activity_daily.chat_messages via the rollup function
    if (role === 'user') {
      try {
        /** @type {Record<string, any>} */
        const client = {
          platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          locale:
            (typeof navigator !== 'undefined' &&
              (navigator.language || navigator.languages?.[0])) ||
            'en-US',
          app_session_id: getAppSessionId(),
          app_version:
            process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
        };
        if (client.app_version === undefined) delete client.app_version;

        /** @type {Record<string, any>} */
        const meta = {
          chat_session_id: session_id,
          message_id: data.id,
          content_length: content.length,
          route: typeof window !== 'undefined' ? window.location?.pathname : '',
        };
        if (meta.route === undefined) delete meta.route;

        // Fire-and-forget insert to user_activity_events
        // Uses 'chat_message' event_type which the daily rollup aggregates
        void supabase
          .from('user_activity_events')
          .insert([
            {
              user_id,
              event_type: 'chat_message',
              session_id: getUuidSessionId(),
              client,
              meta,
            },
          ])
          .select('id')
          .single();
        logger.debug('chatService.createMessage: Recorded chat_message activity event');
      } catch (activityErr) {
        // Swallow errors - analytics should never break UX
        logger.warn('chatService.createMessage: Failed to record activity event', activityErr);
      }
    }

    return mappedMessage;
  } catch (error) {
    const e = /** @type {any} */ (error);
    if (e?.message?.includes('SUPABASE_TIMEOUT')) {
      logger.warn('⏱️ chatService.createMessage: Insert timed out, throwing timeout error');
      throw new Error('Message creation timed out. Please try again.');
    }

    logger.error('❌ chatService.createMessage: Unexpected error:', e);
    throw e;
  }
}

/**
 * Update the updated_at timestamp of a chat session
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID for security
 * @returns {Promise<object|null>} Updated session data or null on non-critical error
 */
export async function updateSessionTimestamp(sessionId, userId) {
  if (!sessionId || !userId) {
    logger.warn('⚠️ chatService.updateSessionTimestamp: Missing sessionId or userId');
    return null;
  }

  logger.info('💬 chatService.updateSessionTimestamp: Updating session timestamp:', sessionId);

  try {
    // First check if the session exists and belongs to the user
    const { data: existingSession, error: selectError } = await supabase
      .from('chat_sessions')
      .select('id, user_id')
      .eq('id', sessionId)
      .eq('user_id', userId)
      .single();

    if (selectError) {
      logger.error(
        '❌ chatService.updateSessionTimestamp: Session not found or access denied:',
        selectError,
      );
      return null;
    }

    if (!existingSession) {
      logger.warn(
        '⚠️ chatService.updateSessionTimestamp: Session does not exist or user does not have access',
      );
      return null;
    }

    // Update the session timestamp
    const { error } = await supabase
      .from('chat_sessions')
      .update({
        updated_at: new Date().toISOString(),
      })
      .eq('id', sessionId)
      .eq('user_id', userId);

    if (error) {
      logger.error('❌ chatService.updateSessionTimestamp: Supabase error:', error);
      // Don't throw error for timestamp updates - it's not critical
      return null;
    }

    logger.info('✅ chatService.updateSessionTimestamp: Updated session timestamp');
    return null; // No row needed; operation success is enough
  } catch (error) {
    logger.error('❌ chatService.updateSessionTimestamp: Unexpected error:', error);
    return null;
  }
}
